import { useState, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { SendIcon, LoaderIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'

interface FDEMessageInputProps {
  onSendMessage: (content: string) => Promise<void>
  disabled: boolean
  isSending: boolean
  isProcessing: boolean
}

export function FDEMessageInput({ onSendMessage, disabled, isSending, isProcessing }: FDEMessageInputProps) {
  const { t } = useTranslation('fde')
  const [content, setContent] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const handleSubmit = useCallback(async () => {
    const trimmed = content.trim()
    if (!trimmed || disabled || isSending || isProcessing) return

    setContent('')
    try {
      await onSendMessage(trimmed)
    } catch {
      setContent(trimmed)
    }

    // Refocus
    setTimeout(() => textareaRef.current?.focus(), 50)
  }, [content, disabled, isSending, isProcessing, onSendMessage])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }, [handleSubmit])

  const isDisabled = disabled || isSending || isProcessing

  return (
    <div className="shrink-0 border-t bg-background px-4 py-3">
      <div className="relative mx-auto max-w-3xl">
        <Textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('chat.inputPlaceholder')}
          disabled={isDisabled}
          rows={1}
          className="min-h-[44px] max-h-[200px] resize-none pr-12"
        />
        <Button
          size="icon"
          variant="ghost"
          className="absolute bottom-1.5 right-1.5 h-8 w-8"
          onClick={handleSubmit}
          disabled={isDisabled || !content.trim()}
        >
          {isSending ? (
            <LoaderIcon className="h-4 w-4 animate-spin" />
          ) : (
            <SendIcon className="h-4 w-4" />
          )}
          <span className="sr-only">{t('chat.send')}</span>
        </Button>
      </div>
    </div>
  )
}
